import { PincodeModalType } from './../../../services/pin-code/pin-code.service';
import { waitForCondition } from 'src/app/utilities/wait-for';
import { AuthFlowState, ScreenType, AuthFlowStateEventType, AuthFlowStateEvent } from './../../../models/pin-code.model';
import { Component, OnInit, ViewChild } from '@angular/core';
import { IonSlides, ModalController } from '@ionic/angular';
import { AuthFlowService } from 'src/app/services/auth-flow/auth-flow.service';
import { ConnectivityService } from 'src/app/services/connectivity/connectivity.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { EventBusService, EventTopic } from 'src/app/services/event-bus/event-bus.service';
import { debounceTime, groupBy, map, mergeAll, Subject } from 'rxjs';
import { VaultService } from 'src/app/services/vault/vault.service';
import { AlertService } from 'src/app/services/alert/alert.service';
import { SentryService, SentrySeverityLevel } from 'src/app/services/sentry/sentry.service';

@Component({
  selector: 'app-pin-code',
  templateUrl: './pin-code.component.html',
  styleUrls: ['./pin-code.component.scss'],
})
export class PinCodeComponent implements OnInit {
  @ViewChild('slider') slides: IonSlides;

  type: PincodeModalType = PincodeModalType.auto;
  currentState: AuthFlowState;
  currentStateIdx: number;
  states: AuthFlowState[] = [];
  isOnline = true;
  onlineSubscription;
  stateDidChange$ = new Subject<AuthFlowStateEvent>();
  movingBack = false;
  movingBackDelay = 700; // ms

  constructor(
    public authFlowSvc: AuthFlowService,
    private modalCtrl: ModalController,
    private connectivitySvc: ConnectivityService,
    private toastSvc: ToastService,
    private translateSvc: TranslateService,
    private eventBusSvc: EventBusService,
    private vaultSvc: VaultService,
    public alertSvc: AlertService,
    private sentrySvc: SentryService
  ) {}

  async ngOnInit() {
    await this.authFlowSvc.init();
    const workflow = await this.authFlowSvc.getCurrentWorkflow(this.type);
    this.states = this.authFlowSvc.getFlowStatesByWorkflow(workflow);

    await this.waitForSlidesInitiated();
    this.handleStateChange();
    this.handleOfflineMode();
    this.sendResetLinkIfNeeded();
    this.loadLastState();
  }

  ionViewDidEnter() {
    this.authFlowSvc.slides = this.slides;
    this.setStateBySlide();
  }

  ionViewWillLeave() {
    this.onlineSubscription.unsubscribe();
    this.eventBusSvc.deleteSubscriber(EventTopic.AuthFlowStateEventType, PinCodeComponent.name);
  }

  async setStateBySlide() {
    await this.waitForSlidesInitiated();
    const previousIdx = await this.slides.getPreviousIndex();
    if (Number.isInteger(previousIdx) && previousIdx < this.states.length) {
      this.states[previousIdx].event$.next({ type: AuthFlowStateEventType.clearPin });
    }

    const currentIdx = await this.slides.getActiveIndex();
    this.setState(currentIdx);
  }

  setState(idx: number) {
    this.currentState = this.states[idx];
    this.currentStateIdx = idx;
    this.authFlowSvc.setPropertiesByAuthFlowState(this.currentState);
  }

  async back() {
    if (this.movingBack) {
      return;
    }
    this.movingBack = true;
    try {
      if ((await this.slides.getActiveIndex()) === 0) {
        await this.modalCtrl.dismiss();
      } else {
        await this.authFlowSvc.back();
        if (this.currentState.temporary) {
          this.states.splice(1, 1);
          await this.slides.update();
        }
      }
    } catch (err) {
      console.error(err);
    }
    // add some delay to prevent 'double click' hack
    setTimeout(() => {
      this.movingBack = false;
    }, this.movingBackDelay);
  }

  showBackbtn(): boolean {
    return this.currentState?.showBackBtn;
  }

  showHeader(): boolean {
    if (!this.currentState) {
      return false;
    }
    return this.showBackbtn() || Boolean(this.currentState.currentScreenHeader);
  }

  getPin(state: AuthFlowState): string {
    if (state.currentScreen === ScreenType.validate) {
      return this.authFlowSvc.validatePin;
    }
    return this.authFlowSvc.pin;
  }

  handleStateChange() {
    this.stateDidChange$
      .pipe(
        groupBy(event => event.type),
        map(group => group.pipe(debounceTime(50))),
        mergeAll()
      )
      .subscribe(async e => {
        if (this.alertSvc.customConfirmationAlert) {
          await this.alertSvc.customConfirmationAlert.dismiss();
        }
        if (e.type === AuthFlowStateEventType.nextState || e.type === AuthFlowStateEventType.prevState) {
          this.setStateBySlide();
        } else if (e.type === AuthFlowStateEventType.finished) {
          this.modalCtrl.dismiss(true);
        } else if (e.type === AuthFlowStateEventType.updateWorkflow) {
          const workflow = await this.authFlowSvc.getCurrentWorkflow(PincodeModalType.auto);
          this.states = this.authFlowSvc.getFlowStatesByWorkflow(workflow);
          await new Promise(resolve => setTimeout(resolve, 500)); // wait for slides to update
          await this.setStateBySlide();
          await this.sendResetLinkIfNeeded();
        } else if (e.type === AuthFlowStateEventType.forgotPin) {
          await this.moveToResetState(true);
        }
      });
    this.eventBusSvc.addSubscriber(
      EventTopic.AuthFlowStateEventType,
      e => {
        this.stateDidChange$.next(e);
      },
      PinCodeComponent.name
    );
  }

  handleOfflineMode() {
    const getToastMessage = async (): Promise<string> => {
      await this.waitForCurrentStateIsSet();
      switch (this.currentState.currentScreen) {
        case ScreenType.locked:
        case ScreenType.reset:
          return 'ToastMessages.resetPinOffline';
      }
      return 'ToastMessages.pinOffline';
    };
    this.onlineSubscription = this.connectivitySvc.userOnline.subscribe(async online => {
      this.isOnline = online;
      if (online) await this.toastSvc.closeOfflineToast();
      else await this.toastSvc.showOfflineToast(this.translateSvc.instant(await getToastMessage()), null, { position: 'top' });
    });
  }

  async loadLastState() {
    if (this.authFlowSvc.pincode.resetPrompted && !this.authFlowSvc.locked && !this.authFlowSvc.resetToken) {
      await this.waitForCurrentStateIsSet();
      await this.moveToResetState(false);
    }
  }

  async moveToResetState(sendResetLink: boolean) {
    if (sendResetLink) {
      const sent = await this.sendResetLink();
      if (!sent) {
        return;
      }
    }
    const resetState = this.authFlowSvc.getResetFlowState();
    if (sendResetLink) {
      resetState.resetLinkSentCount = this.currentState.resetLinkSentCount;
      this.currentState.resetLinkSentCount = 0;
    }
    this.states.splice(1, 0, resetState);
    await this.slides.update();
    await this.authFlowSvc.next();
    await this.setStateBySlide();
  }

  async sendResetLinkIfNeeded() {
    await this.waitForCurrentStateIsSet();
    if (this.currentState.currentScreen === ScreenType.locked && !this.vaultSvc.getResetLinkSentAt()) {
      await this.sendResetLink();
    }
  }

  async sendResetLink(showAlert = false): Promise<boolean> {
    const loadingAlert = await this.alertSvc.createLoadingAlert();
    await loadingAlert.present();
    let success = false;
    try {
      await this.vaultSvc.sendResetLink();
      this.currentState.resetLinkSentCount++;
      success = true;
    } catch (err) {
      this.sentrySvc.setMessageForSentryWithErrorInfo('Failed to send reset pincode', SentrySeverityLevel.Error, {
        error: err,
      });
    }
    await loadingAlert.dismiss();
    if (showAlert && success) {
      await this.alertSvc.presentCustomConfirmation(
        this.translateSvc.instant('SuspendPage.resendHeader'),
        '',
        this.translateSvc.instant('SuspendPage.resendDescription'),
        '',
        this.translateSvc.instant('CommonTexts.dismiss'),
        '',
        true
      );
    }
    return success;
  }

  private async waitForSlidesInitiated() {
    await waitForCondition(() => Boolean(this.slides) && this.states.length > 0, true, 10);
  }
  private async waitForCurrentStateIsSet() {
    await waitForCondition(() => Boolean(this.currentState) && Boolean(this.currentState.id), true, 10);
  }
}
